import { eff } from "@/core/utils/effect-ts-abbr";
import NotFoundComponent from "@/pages/not-found/not-found.vue";
import { routerModel } from "@/router/router.model";
import { AppLanguageCode, ByAppLangCode } from "@/shared/i18n/translations";
import { pageTitle } from "@/shared/models/page-title";
import {
    createRouter,
    createWebHistory,
    RouteComponent,
    Router,
    RouteRecordRaw,
} from "vue-router";
import { sessionModel } from "@/shared/state/session.model";
import { getAuthRedirectService } from "@/core/services/auth-redirects.service";
import { getEnvValue } from "@/shared/models/environment";

export interface AppRoute {
    lang: AppLanguageCode;
    template: RouteComponent | eff.fn.Lazy<RouteComponent>;
    path: string;
    name: string;
    id: number;
    titleTranslateKey: string;
    slug: string;
    sameRouteByLang: ByAppLangCode<number /* id */>;
}

function router(appRoutes: ByAppLangCode<eff.dict.Dictionary<AppRoute>>): Router {
    const defaultRoutes: RouteRecordRaw[] = [
        {
            path: "/public/vehicles",
            component: () => import("@/pages/public/vehicles/public-vehicles.vue"),
            meta: {
                titleTranslateKey: `pagesTitles.ourcars`,
            },
            props: (route) => ({
                groupingmode: Number(route.params.groupingmode),
                hashcode: Number(route.params.hashcode),
            }),
        },
        {
            path: "/public/vehicles-grouped",
            component: () =>
                import("@/pages/public/vehicles-grouped/public-vehicles-grouped.vue"),
            meta: {
                titleTranslateKey: `pagesTitles.ourcars`,
            },
            props: (route) => ({
                groupingmode: Number(route.params.groupingmode),
                hashcode: Number(route.params.hashcode),
            }),
        },
        {
            path: "/public/vehicle-details/:id",
            component: () =>
                import("@/pages/public/vehicle-details/public-vehicle-details.vue"),
        },
        {
            path: "/auth/vehicles",
            component: () => import("@/pages/auth/vehicles/vehicles.vue"),
            meta: {
                titleTranslateKey: `pagesTitles.ourcars`,
            },
            props: (route) => ({
                groupingmode: Number(route.params.groupingmode),
                hashcode: Number(route.params.hashcode),
            }),
        },
        {
            path: "/auth/vehicles-grouped",
            component: () => import("@/pages/auth/vehicles-grouped/vehicles-grouped.vue"),
            meta: {
                titleTranslateKey: `pagesTitles.ourcars`,
            },
            props: (route) => ({
                groupingmode: Number(route.params.groupingmode),
                hashcode: Number(route.params.hashcode),
            }),
        },
        {
            path: "/auth/vehicle-details/:id",
            component: () => import("@/pages/auth/vehicle-details/vehicle-details.vue"),
            meta: {
                isProtected: true,
            },
        },
        {
            path: "/car-print/:id",
            component: () =>
                import(
                    /* webpackChunkName: "vehicle-details" */ "../pages/car-print/car-print.vue"
                ),
        },
        {
            path: "/login",
            component: () =>
                import(/* webpackChunkName: "login" */ "../core/auth/views/Login.vue"),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/forgot-password",
            component: () =>
                import(
                    /* webpackChunkName: "login" */ "../core/auth/views/ForgotPassword.vue"
                ),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/reset-password",
            component: () =>
                import(
                    /* webpackChunkName: "login" */ "../core/auth/views/ResetPassword.vue"
                ),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/signup-dealer",
            component: () =>
                import(
                    /* webpackChunkName: "SignUpDealer" */ "../core/auth/views/SignUpDealer.vue"
                ),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/signup-supplier",
            component: () =>
                import(
                    /* webpackChunkName: "SignUpSupplier" */ "../core/auth/views/SignUpSupplier.vue"
                ),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/invite/:token(.*)*",
            component: () =>
                import(
                    /* webpackChunkName: "SignUpSupplier" */ "../core/auth/views/SignUpSupplier.vue"
                ),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/auth/my-carconnex/my-orders/:order_status/order-details/:order_id",
            name: "order-details",
            component: () =>
                import(
                    "../dashboard/my-orders/orders-table/table-item/order-details.vue"
                ),
            meta: {
                titleTranslateKey: `pagesTitles.dashboardMyOrders`,
                mainClass: "order-details",
            },
        },
        {
            path: "/sign-up/:clientId/:companyEmail",
            component: () =>
                import(
                    /* webpackChunkName: "SignUpSupplier" */ "../core/auth/views/SignUpSupplier.vue"
                ),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/signup/thanks",
            component: () =>
                import(/* webpackChunkName: "Thanks" */ "../core/auth/views/Thanks.vue"),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/auth/my-carconnex",
            component: () =>
                import(
                    /* webpackChunkName: "dashboard" */
                    "../dashboard/dashboard.vue"
                ),
            children: [
                {
                    path: "",
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-welcome-page" */
                            "../dashboard/welcome-page/welcome-page.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboard`,
                        mainClass: "welcome",
                    },
                },
                {
                    path: "my-account",
                    redirect: "/auth/my-carconnex/my-account/contact-info",
                    children: [
                        {
                            path: ":tab",
                            name: "app-my-account",
                            components: {},
                        },
                    ],
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-my-account" */
                            "../dashboard/my-account/my-account.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboardMyAccount`,
                        mainClass: "account",
                    },
                },
                {
                    path: "my-company",
                    redirect: "/auth/my-carconnex/my-company/company-info",
                    children: [
                        {
                            path: ":tab",
                            name: "app-my-company",
                            components: {},
                        },
                    ],
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-my-account" */
                            "../dashboard/my-company/my-company.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboardMyAccount`,
                        mainClass: "account",
                    },
                },
                {
                    path: "my-settings",
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-my-settings" */
                            "../dashboard/my-settings/my-settings.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboardMySettings`,
                        mainClass: "settings",
                    },
                },
                {
                    path: "my-favorites",
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-my-favorites" */
                            "../dashboard/my-favorites/my-favorites.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboardMyFavorites`,
                        mainClass: "favorites",
                    },
                },
                {
                    path: "my-car-history",
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-my-car-history" */
                            "../dashboard/my-car-history/my-car-history.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboardMyCarHistory`,
                    },
                },
                {
                    path: "my-orders/",
                    redirect: "/auth/my-carconnex/my-orders/to_approve",
                    children: [
                        {
                            path: ":tab",
                            name: "app-my-orders",
                            components: {},
                        },
                    ],
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-my-orders" */
                            "../dashboard/my-orders/my-orders.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboardMyOrders`,
                        mainClass: "orders",
                    },
                },
                {
                    path: "my-orders/all",
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-my-orders" */
                            "../dashboard/my-orders/my-orders.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboardMyOrders`,
                        mainClass: "orders",
                    },
                },
                {
                    path: "my-orders/:order_status",
                    component: () =>
                        import(
                            /* webpackChunkName: "dashboard-my-orders" */
                            "../dashboard/my-orders/my-orders.vue"
                        ),
                    meta: {
                        titleTranslateKey: `pagesTitles.dashboardMyOrders`,
                        mainClass: "orders",
                    },
                    // children: [{
                    //     path: "order-details",
                    //     name: "order-details",
                    //     component: () =>
                    //         import(
                    //             /* webpackChunkName: "dashboard-my-orders" */
                    //             "../dashboard/my-orders/orders-table/table-item/order-details.vue"
                    //             ),
                    //     meta: {
                    //         titleTranslateKey: "pagesTitles.dashboardMyOrders",
                    //         mainClass: "orders",
                    //     },
                    // }]
                },
            ],
            meta: {
                isProtected: true,
            },
        },
        {
            path: "/shopping-cart",
            component: () =>
                import(
                    /* webpackChunkName: "cart" */ "../pages/shopping-cart/shopping-cart.vue"
                ),
            meta: {
                titleTranslateKey: `pagesTitles.cart`,
                isProtected: true,
            },
        },
        {
            path: "/work-in-progress",
            component: () =>
                import(
                    /* webpackChunkName: "cart" */ "../pages/work-in-progress/work-in-progress.vue"
                ),
            meta: {
                titleTranslateKey: `pagesTitles.workInProgress`,
            },
        },
        {
            path: "/verify-email/:userid/:token",
            component: () =>
                import(
                    /* webpackChunkName: "verify-email" */ "../pages/verify-email/verify-email.vue"
                ),
            meta: {
                authPage: true,
            },
        },
        {
            path: "/e5fe329e-31bb-4909-81c4-39a997b02ed2/:id/:role/:clientId/:companyEmail",
            name: "app-role-validation",
            component: () =>
                import(
                    /* webpackChunkName: "role-validation" */ "../pages/role-validation/role-validation.vue"
                ),
        },
        {
            path: "/terms-and-conditions",
            component: () =>
                import(
                    /* webpackChunkName: "terms-and-conditions" */ "../core/terms-and-conditions/terms-and-conditions.vue"
                ),
        },
        ...(getEnvValue("BRAND_LABEL") === "astara"
            ? [
                  {
                      path: "/:lang/contact-us",
                      component: () =>
                          import(
                              /* webpackChunkName: "astara-contact-us" */ "../core/contacts/views/astara-contact-us.vue"
                          ),
                  },
              ]
            : []),
        {
            path: "/:pathMatch(.*)*",
            name: "not-found",
            component: NotFoundComponent,
        },
    ];

    const routes: RouteRecordRaw[] = eff.fn.pipe(
        appRoutes,
        eff.dict.map(
            eff.fn.flow(
                eff.dict.toArray,
                eff.array.map(({ tuple: [, route] }) => route),
            ),
        ),
        eff.dict.toArray,
        eff.array.map(({ tuple: [, routes] }) => routes),
        eff.array.flatten,
        eff.array.map(({ path, name, template, titleTranslateKey, slug }) => {
            return {
                path,
                name,
                component: template,
                meta: {
                    titleTranslateKey,
                    slug,
                },
            } as unknown as RouteRecordRaw;
        }),
        eff.array.concat(defaultRoutes),
        eff.array.toMutable,
    );

    routerModel.router = createRouter({
        history: createWebHistory(process.env.BASE_URL),
        routes,
    });

    routerModel.router.beforeEach((to, from, next) => {
        // pageLoaderModel.toggle(true);
        const isAuthenticated = !!sessionModel.sessionDto.value;
        if (typeof to.meta?.titleTranslateKey === "string") {
            pageTitle.value = to.meta.titleTranslateKey;
        }

        const loginRequiredBrands = ["astara"];

        const isLoginRequiredBrand = loginRequiredBrands.includes(
            getEnvValue("BRAND_LABEL") ?? "",
        );

        if (
            (to.meta.isProtected && !isAuthenticated) ||
            (isLoginRequiredBrand && !isAuthenticated && !to.meta.authPage)
        ) {
            /* &&  from.path !== "/login" <-- uncomment & add after authorization will work correctly */
            const authRedirectService = getAuthRedirectService();
            authRedirectService.afterLoginUrl = to.fullPath;
            next({
                path: "/login",
            });
        } else {
            next();
        }
    });

    return routerModel.router;
}

export default router;
